import React from 'react';
import { Formik } from 'formik';
import { makeStyles } from 'tss-react/mui';
import { CalendarApi } from '@fullcalendar/react';
import { useAlert } from '../../../../AlertProvider';

import { AppointmentRecurring, AppointmentSlotDuration } from '@uplift/api';
import { useCurrentuser, useTimezone } from '@uplift/contexts';
import { CalendarDateService } from '@uplift/services';
import { ProviderMedicalRole, Sex } from '@uplift/types';
import { useL10n } from '@uplift/hooks';

import { CancelButton } from '../../../../Form';
import { ModalActions, ModalContent, ModalTitle } from '../../../../Modal';

import { CreateAppointmentFormValues } from '../AddSessionForm.types';
import { useAddSessionValidationSchema } from '../AddSessionForm.validation';
import { AddSessionFormSubmitButton } from '../components/AddSessionFormSubmitButton';
import { useAddSession } from '@uplift/queries';
import { AddSessionForm, AddSessionFormProps } from './AddSessionForm.component';
import { useQueryClient } from '@tanstack/react-query';
import { AppointmentDurationMappingMinutesDuration } from '../../../../Calendar/ScheduleCalendar/ScheduleCalendarMappings';
import { DateFormats, providerScheduleViewTypeRoute } from '@uplift/constants';


const useStyles = makeStyles()({
  root: {},
  content: {
    paddingTop: '0 !important',
    maxHeight: 'calc(100vh - 150px)',
  }
});

export type AddSessionFormType = {
  isPsychiatrist: boolean;
  isNewPatient: boolean;
  existingPatient: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    preferredName?: string | undefined;
  } | undefined;
  invitedPatient: {
    firstName: string;
    lastName: string;
    email: string;
    sex?: Sex;
    birthDate?: string;
  };
  duration: AppointmentSlotDuration;
  repeats: AppointmentRecurring;
  date: string | null;
  startTime: string | null;
  notes: string;
};

export type AddSessionFormFormikProps = {
  open?: boolean;
  onClose: () => void;
  onSuccess: () => Promise<void>;
  onDateChange?: AddSessionFormProps['onDateChange'];
  onTimeChange?: AddSessionFormProps['onTimeChange'];
  onEventParamsChange?: AddSessionFormProps['onEventParamsChange'];

  calendarApi?: CalendarApi;
  newEvent?: { start?: string; end?: string };
};


export const AddSessionFormFormik: React.FC<AddSessionFormFormikProps> = (props) => {
  const queryClient = useQueryClient();
  const l10n = useL10n();
  const { classes } = useStyles();
  const timezone = useTimezone();
  const alert = useAlert();
  const me = useCurrentuser();
  const shouldInvitePatient = providerScheduleViewTypeRoute.useQueryParam('createAppointment') === 'invite';
  const isPsychiatrist = me.medicalRole === ProviderMedicalRole.psychiatrist;

  const startDateObj = React.useMemo(() => props.newEvent?.start ? CalendarDateService.date(props.newEvent?.start).tz(timezone) : undefined, [ timezone, props.newEvent?.start ]);
  const startDate = React.useMemo(() => startDateObj?.utc().format(DateFormats.ISODayjs), [ startDateObj ]);
  const initialDateValue = React.useMemo(() => startDateObj ? startDateObj.format(DateFormats.ISO_DATE) : undefined, [ startDateObj ]);

  const validator = useAddSessionValidationSchema();
  const { addSession } = useAddSession();

  const onSubmit = async (values: CreateAppointmentFormValues, helpers: { setStatus: (e: any) => void }) => {
    helpers.setStatus({
      errorDisplayed: false,
      errorDescription: '',
      submissionBlocked: false,
    });

    try {
      const { patient } = await addSession(values);

      await props.onSuccess?.();
      queryClient.invalidateQueries([ 'calendar' ]);
      if (values.isNewPatient) {
        alert.success(l10n.schedule.createAppointment.invitationSent(patient.preferredName || patient.firstName));
      } else {
        if (values.repeats === AppointmentRecurring.none) {
          alert.success(l10n.schedule.createAppointment.sessionAdded);
        } else {
          alert.success(l10n.schedule.createAppointment.sessionsAdded);
        }
      }
    } catch (e) {
      if (e instanceof Error) {
        helpers.setStatus({
          errorDisplayed: true,
          errorDescription: e?.message || 'unknown',
          submissionBlocked: false,
        });
      } else {
        helpers.setStatus({
          errorDisplayed: true,
          errorDescription: 'unknown',
          submissionBlocked: false,
        });
      }

    }
  };

  const duration = React.useMemo(() => {
    if (!props.newEvent?.end) {
      if (isPsychiatrist) {
        return AppointmentSlotDuration.thirty;
      }

      return AppointmentSlotDuration.sixty;
    }

    const minutes = CalendarDateService.date(props.newEvent?.end).diff(props.newEvent?.start, 'minutes');

    return AppointmentDurationMappingMinutesDuration[minutes];
  }, [ props.newEvent?.start, props.newEvent?.end, isPsychiatrist ]);

  const initialValues: AddSessionFormType = {
    isPsychiatrist: isPsychiatrist,
    isNewPatient: shouldInvitePatient ?? false,
    existingPatient: undefined,
    invitedPatient: {
      firstName: '',
      lastName: '',
      email: '',
      sex: undefined,
      birthDate: undefined
    },
    duration: duration,
    repeats: AppointmentRecurring.none,
    date: initialDateValue ?? null,
    startTime: startDate ?? null,
    notes: '',
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={validator}
      initialStatus={{
        errorDisplayed: false,
        errorDescription: '',
        submissionBlocked: false,
      }}
    >
      <>
        <ModalTitle onClose={props.onClose}>
          {l10n.schedule.createAppointment.newSession}
        </ModalTitle>
        <ModalContent className={classes.content}>
          <AddSessionForm
            open={props.open}
            newEvent={props.newEvent}
            onDateChange={props.onDateChange}
            onTimeChange={props.onTimeChange}
            onEventParamsChange={props.onEventParamsChange}
          />
        </ModalContent>
        <ModalActions>
          <CancelButton
            // validation error is changing modal height, so CLOSE button is moving down and `onClick` event is not dispatching
            // onMouseDown used because we need to handle click before validation error shown
            onMouseDown={props.onClose}
            size="medium"
          >
            {l10n.general.cancel}
          </CancelButton>
          <AddSessionFormSubmitButton size="medium" color="secondary">
            {l10n.general.save}
          </AddSessionFormSubmitButton>
        </ModalActions>
      </>
    </Formik>
  );
};
